import { DBCollection } from '@/lib/utils/constants-shared';
import { BIBLE_BOOKS_SORT_ORDER } from '@/lib/utils/bible-utils';
import { fetchItemsFromDB } from '@/lib/utils/db-service';
import { requireUser } from '@/lib/utils/utils';
import type { Auth } from 'firebase/auth';
import { collection, type Firestore, query, type Timestamp } from 'firebase/firestore';
import { parseRef } from '@/lib/utils/reference-utils';

// Re-export parseRef from shared utilities for backward compatibility
export { parseRef };

import { fetchCollectionFromStorage } from '@/lib/utils/storage-bulk-data';
import type { FirebaseStorage } from 'firebase/storage';

export async function fetchPassagesFromDB(_auth: Auth, db: Firestore, storage?: FirebaseStorage): Promise<Passage[]> {
    async function fallback() {
        const dataQuery = query(collection(db, DBCollection.passages));
        const items = await fetchItemsFromDB<FirebasePassage<Timestamp>>(dataQuery);
        return items.map(item => ({
            ...item,
            dateCreated: item.dateCreated.toDate(),
            dateUpdated: item.dateUpdated.toDate()
        }));
    }

    if (storage) {
        // DBCollection.passages is 'sections'
        return await fetchCollectionFromStorage<Passage>(
            storage,
            `bulk-data/${DBCollection.passages}.json`,
            fallback
        );
    }

    return await fallback();
}

export function filterPassages(items: Passage[], filters: PassageFilters): Passage[] {
    if (!filters.book) {
        return items;
    }
    return items.filter(item => {
        if (filters.book !== undefined && filters.book !== item.book) {
            return false;
        }
        return true;
    });
}

export function sortPassages(items: Passage[]): Passage[] {
    return [...items].sort((a, b) => {
        if (a.book === b.book) {
            const aRef = parseRef(a.ref);
            const bRef = parseRef(b.ref);

            // If either ref is invalid, fall back to string comparison
            if (!aRef || !bRef) {
                return a.ref.localeCompare(b.ref);
            }

            if (aRef.chapterStart === bRef.chapterStart) {
                return aRef.verseStart < bRef.verseStart ? -1 : 1;
            }
            return aRef.chapterStart < bRef.chapterStart ? -1 : 1;
        }
        return BIBLE_BOOKS_SORT_ORDER[a.book] < BIBLE_BOOKS_SORT_ORDER[b.book] ? -1 : 1;
    });
}

export function getPassagesByBookMap(items: Passage[]): Map<string, Passage[]> {
    const byBookMap = new Map<string, Passage[]>();
    items.forEach(item => {
        if (!byBookMap.has(item.book)) {
            byBookMap.set(item.book, []);
        }
        byBookMap.get(item.book)!.push(item);
    });
    return byBookMap;
}

export function getPassageByIdMap(items: Passage[]): Map<string, Passage> {
    const byIdMap = new Map<string, Passage>();
    items.forEach(item => byIdMap.set(item.id, item));
    return byIdMap;
}